import {
  Auth,
  getAuth,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import firebase from "firebase/compat/app";
import { clearReduxStore } from "@/lib/utils";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "@firebase/app-check";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

let firebaseApp: firebase.app.App | null = null;
let auth: Auth | null = null;

export function initializeFirebase() {
  if (!firebaseApp) {
    firebaseApp = firebase.initializeApp(firebaseConfig);
    auth = getAuth(firebaseApp);

    // Enable App Check
    initializeAppCheck(firebaseApp, {
      provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });

    auth = getAuth(firebaseApp);
  }
  return { firebaseApp, auth };
}

export async function login(email: string, password: string) {
  const { auth } = initializeFirebase(); // Ensure Firebase is initialized
  if (!auth) throw new Error("Firebase Auth is not initialized.");
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error("Login failed", error);
    throw error;
  }
}

export async function loginWithGoogle() {
  const { auth } = initializeFirebase(); // Ensure Firebase is initialized
  if (!auth) throw new Error("Firebase Auth is not initialized.");
  const provider = new GoogleAuthProvider();
  provider.addScope("email");
  try {
    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error) {
    console.error("Google login failed", error);
    throw error;
  }
}

export async function getUser() {
  const { auth } = initializeFirebase(); // Ensure Firebase is initialized
  if (!auth) throw new Error("Firebase Auth is not initialized.");
  return auth.currentUser;
}

export async function getUserToken() {
  const { auth } = initializeFirebase(); // Ensure Firebase is initialized
  if (!auth) throw new Error("Firebase Auth is not initialized.");
  const token = await auth.currentUser?.getIdToken();
  return token;
}

export async function logout() {
  const { auth } = initializeFirebase(); // Ensure Firebase is initialized
  if (!auth) throw new Error("Firebase Auth is not initialized.");
  // clear token from local storage
  localStorage.removeItem("token");
  clearReduxStore();
  await auth.signOut();
  // send user to login page if not already there
  if (window.location.pathname !== "/") window.location.href = "/";
}
